import React from 'react';
import { Modal as AntModal, Descriptions, message, Typography } from 'antd';
import {
  useModalMethods,
  useModalWatch,
} from '../../../../shared/packages/modalManager';
import { modalNames } from '../../../../shared/configs/modal';
import {
  FieldArrayProvider,
  FormControllerProvider,
  getValueFromFormState,
  useFieldArrayState,
  useFormControllerState,
  useFormMethods,
} from '../../../../../../helpers/formManager';
import { useParsedAcceptanceSlug } from '../../../../services/queries/acceptanceData';
import FetchDataWrapper from '../../../../../../components/FetchDataWrapper';
import ByIntervalSetupIntervals from '../../../../controllers/ByIntervalSetupIntervals';
import SelectIntervalAcceptanceType from '../../../../controllers/SelectIntervalAcceptanceType';
import SelectIntervalAvailability from '../../../../controllers/SelectIntervalAvailability';
import { fieldNames } from '../../../../shared/configs/acceptanceForm';
import { formatDate } from '../../../../shared/packages/dateTime';
import { defaultIntervalMode } from '../../../../shared/configs/selectionData/intervalModes';
import { defaultIntervalAvailability } from '../../../../shared/configs/selectionData/intervalAvailabilities';
import { useAddCalendarIntervals } from '../../../../api/addCalendarIntervals';
import { useAcceptanceViewCalendarInvalidate } from '../../../../api/acceptanceViewCalendar';
import { useTimeIntervalsQuery } from '../../../../api/timeIntervals';
import { TimeIntervalsProvider } from '../../../../services/timeIntervals';
import styles from './index.module.scss';

const modalName = modalNames.addCalendarIntervals;

const intervalsName = fieldNames.deskScheduleSetting.intervals;
const {
  duration: durationKey,
  intervalId: intervalIdKey,
  mode: intervalTypeKey,
  availability: availabilityKey,
  active: activeKey,
} = fieldNames.scheduleSettingInterval;

const options = {
  defaultValues: {
    [intervalsName]: [
      {
        [activeKey]: true,
        [intervalTypeKey]: defaultIntervalMode,
        [availabilityKey]: defaultIntervalAvailability,
      },
    ],
  },
};

const AddCalendarIntervalsModal = () => {
  return (
    <FormControllerProvider options={options}>
      <Modal />
    </FormControllerProvider>
  );
};

const Modal = () => {
  const { isOpen, props } = useModalWatch({ name: modalName });
  const { close } = useModalMethods();

  const { deskIds, existingIntervalIds, deskNames, targetDate } = props ?? {};

  const { acceptanceId } = useParsedAcceptanceSlug();

  const { handleSubmit, reset } = useFormMethods();

  const invalidateCalendarView = useAcceptanceViewCalendarInvalidate();

  const closeModal = () => {
    close(modalName);
  };

  const { mutate, isLoading } = useAddCalendarIntervals({
    onSuccess: (data) => {
      if (data?.success === false) {
        message.error(data.message || 'Не удалось добавить интервалы');
        return;
      }

      invalidateCalendarView();
      closeModal();
    },
  });

  const handleOk = (values) => {
    const intervals = values[intervalsName];

    if (!intervals) {
      closeModal();
      return;
    }

    const payload = {
      acceptanceId,
      deskIds,
      targetDate,
      intervals: createIntervalsPayload(intervals),
    };

    mutate(payload);
  };

  const afterClose = () => {
    reset();
  };

  return (
    <AntModal
      title="Добавление интервалов"
      visible={isOpen}
      onCancel={closeModal}
      onOk={handleSubmit(handleOk)}
      afterClose={afterClose}
      centered
      maskClosable={false}
      confirmLoading={isLoading}
    >
      <FetchData enabled={isOpen}>
        <Descriptions>
          <Descriptions.Item label="Дата">
            {targetDate ? formatDate(targetDate) : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Столы">
            {deskNames?.join(', ') ?? ''}
          </Descriptions.Item>
        </Descriptions>
        <div className={styles.intervalsContainer}>
          <Typography.Text strong>Настройка интервалов</Typography.Text>
          <TimeIntervalsProvider>
            <FieldArrayProvider
              name={intervalsName}
              rules={{ required: 'Настройте интервалы' }}
            >
              <List initialIds={existingIntervalIds} />
              <ErrorBlock />
            </FieldArrayProvider>
          </TimeIntervalsProvider>
        </div>
      </FetchData>
    </AntModal>
  );
};

export default AddCalendarIntervalsModal;

function FetchData({ enabled, children }) {
  const { data, isFetching, refetch } = useTimeIntervalsQuery({
    options: { enabled },
  });

  return (
    <FetchDataWrapper
      data={data}
      isFetching={isFetching}
      refetch={refetch}
      errMsg="Не удалось загрузить список доступных интервалов"
    >
      {children}
    </FetchDataWrapper>
  );
}

function List({ initialIds }) {
  const fields = useFieldArrayState();

  const durationAndTimeConfig = { createDurationName, createIntervalIdName };

  return (
    <ByIntervalSetupIntervals
      fields={fields}
      durationAndTimeConfig={durationAndTimeConfig}
      renderIntervalSettings={RenderIntervalSettings}
      initialIds={initialIds}
    />
  );
}

function ErrorBlock() {
  const { errors } = useFormControllerState({
    name: intervalsName,
    exact: true,
  });
  const errorData = getValueFromFormState(errors, intervalsName);

  if (!errorData?.hasOwnProperty?.('root')) return null;

  return (
    <Typography.Text type="danger">{errorData.root.message}</Typography.Text>
  );
}

function RenderIntervalSettings({ index }) {
  return (
    <div className="intervalInnerFlexContainer">
      <SelectIntervalAcceptanceType name={createIntervalTypeName(index)} />
      <SelectIntervalAvailability
        name={createIntervalAvailabilityName(index)}
      />
    </div>
  );
}

function createIntervalsPayload(intervals) {
  return intervals.map((data) => ({
    [intervalIdKey]: data[intervalIdKey].context.id,
    [intervalTypeKey]: data[intervalTypeKey],
    [availabilityKey]: data[availabilityKey],
  }));
}

function createDurationName(index) {
  return `${intervalsName}.${index}.${durationKey}`;
}

function createIntervalIdName(index) {
  return `${intervalsName}.${index}.${intervalIdKey}`;
}

function createIntervalTypeName(index) {
  return `${intervalsName}.${index}.${intervalTypeKey}`;
}

function createIntervalAvailabilityName(index) {
  return `${intervalsName}.${index}.${availabilityKey}`;
}
